import * as React from "react";
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { SecurityAppSecurityEventsHistogram } from "@graylog/enterprise-api";
import FetchErrorAlert from "components/common/FetchErrorAlert";
import Spinner from "components/common/Spinner";
import useMitreTactics from "security-app/components/SecurityEvents/Alerts/MitreCategories/useMitreTactics";
import useUserDateTime from "hooks/useUserDateTime";
import { categoryKey } from "security-app/components/SecurityEvents/Alerts/Constants";
import { parseSearchParams } from "security-app/hooks/api/securityEventsAPI";
import useOnRefresh from "components/common/PaginatedEntityTable/useOnRefresh";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import Histogram, { otherBucket } from "./Histogram";
const fetchTacticsHistogram = async (searchParams, timezone) => {
  const result = await SecurityAppSecurityEventsHistogram.tacticsHistogram(parseSearchParams(searchParams, timezone));
  return Object.fromEntries(result.tactics.map((tactic) => [tactic.id, tactic]));
};
const generateChart = (tactics, buckets) => {
  const sortedTactics = [
    ...tactics.sort((t1, t2) => {
      var _a, _b, _c, _d;
      return ((_b = (_a = buckets[t2[0]]) == null ? void 0 : _a.count) != null ? _b : 0) - ((_d = (_c = buckets[t1[0]]) == null ? void 0 : _c.count) != null ? _d : 0);
    }),
    otherBucket
  ];
  const x = sortedTactics.map((tactic) => tactic[0]);
  const text = sortedTactics.map((tactic) => tactic[1]);
  const y = sortedTactics.map((tactic) => {
    var _a, _b;
    return (_b = (_a = buckets[tactic[0]]) == null ? void 0 : _a.count) != null ? _b : 0;
  });
  return { x, y, text };
};
const EventsTacticsGraph = ({ data, tactics, onClickMarker }) => {
  const { x, y, text } = useMemo(() => generateChart(tactics, data), [tactics, data]);
  return /* @__PURE__ */ React.createElement(Histogram, { onClickMarker, x, y, text, type: "Tactics" });
};
const EventsTacticsHistogram = ({ searchParams, setFilters }) => {
  const { tactics, isLoadingTactics } = useMitreTactics();
  const { userTimezone } = useUserDateTime();
  const {
    data,
    isInitialLoading: isLoadingHistogram,
    refetch,
    isError,
    error
  } = useQuery({
    queryKey: ["events", "tactics", searchParams, userTimezone],
    queryFn: () => fetchTacticsHistogram(searchParams, userTimezone)
  });
  const sendTelemetry = useSendTelemetry("security-events");
  useOnRefresh(refetch);
  const onClickMarker = useCallback(
    ({ x }) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.SECURITY_EVENTS.MITRE_ATTACK_WIDGET.DRILLDOWN, {
        app_action_value: { drilldown_into: "tactic" }
      });
      if (x !== otherBucket[0]) {
        setFilters(searchParams.filters.set(categoryKey, [x]));
      }
    },
    [searchParams.filters, sendTelemetry, setFilters]
  );
  if (isLoadingTactics || isLoadingHistogram) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  if (isError || !data) {
    return /* @__PURE__ */ React.createElement(FetchErrorAlert, { message: "Fetching tactics histogram failed", error });
  }
  return /* @__PURE__ */ React.createElement(EventsTacticsGraph, { data, tactics, onClickMarker });
};
export default EventsTacticsHistogram;
